import React, {useEffect, useState} from "react"
import {KeyValue} from "@/library/utilities/general"
import Form from "@/components/forms/form"
import useChangelog from "@/components/changelog/context/_hook.changelog"
import FormTitle from "@/components/forms/form-design/form-title"
import FormInput from "@/components/forms/form-input"
import {InputType} from "@/components/forms/_form-helpers"
import FormSection from "@/components/forms/form-design/form-section"
import {Product, ProductVersion, ProductVersionCreate, ProductVersionUpdate} from "@/library/api/models/changelog"
import FormSectionHeading from "@/components/forms/form-design/form-section-heading"
import Button from "@/components/ui-kit/button"
import {useMutation, useQueryClient} from "@tanstack/react-query"
import {pckProductVersionCreate, pckProductVersionUpdate} from "@/library/api/changelog-requests"
import useAuth from "@/library/auth/_hook.auth"
import {useNavigate} from "@tanstack/react-router"
import {ApiResponseBody} from "@/library/api/models/api"

enum FormMode {
    EDIT,
    CREATE
}

export default function ChangelogForm<T extends ProductVersionCreate & { id?: number }>(
    {
        initialValues,
        closeCallback,
    }: {
        initialValues: T
        closeCallback?: () => void
    }
) {
    const {token} = useAuth()
    const queryClient = useQueryClient()
    const {products, currentProduct} = useChangelog()
    const [formData, setFormData] = useState<T>(initialValues)
    const [product, setProduct] = useState<Product>(products.find(p => p.id == initialValues.productId) ?? products.first())
    const navigate = useNavigate()

    const mode: FormMode = initialValues.id
        ? FormMode.EDIT
        : FormMode.CREATE

    const mutation = useMutation({
        mutationFn: async (data: T) => {

            data.changes = data.changes.map(change => {
                if (!change)
                    return

                return change
            }).filter(change => change != undefined)

            return mode == FormMode.CREATE
                ? await pckProductVersionCreate(data, token)
                : await pckProductVersionUpdate(data as ProductVersionUpdate, token)
        },
        onSuccess: (response: ApiResponseBody<ProductVersion>) => {
            queryClient.refetchQueries({queryKey: ['versions', product.refKey]}).then(() => {
                closeForm()

                console.log(response)

                if (response.data?.number)
                    navigate({
                        hash: response.data.number
                    }).then()
            })
        }
    })

    useEffect(() => {
        if (!product.branches.some(b => b.id == formData.branchId))
            setFormData(current => {

                return {
                    ...current,
                    branchId: product.branches.last().id
                }
            })
    }, [product])

    useEffect(() => {
        if (product.id != formData.productId) {
            const product = products.find(p => p.id == formData.productId)
            if (!product)
                return

            setProduct(() => product)
        }
    }, [formData])

    if (!currentProduct)
        return <div className="alert alert--error">Noe gikk galt :(</div>

    const closeForm = () => {
        closeCallback?.()
    }

    return <Form<T>
        className="create-new-changelog-form"
        defaultState={formData}
        passedState={[formData, setFormData]}
        onChange={data => {
            setFormData(() => data)
        }}
        onSubmit={(_, data) => {
            mutation.mutate(data)
        }}
    >
        {mode == FormMode.EDIT
            ? <FormTitle
                heading={`Redigerer ${products.find(p => p.id == initialValues.productId)?.name} versjon ${initialValues.number}`}
                description="Gjør endringene du ønsker i feltene under og lagre."
            />
            : <FormTitle
                heading={`Ny versjon for ${products.find(p => p.id == formData?.productId)?.name}`}
                description="Opprett en ny versjon med en oversikt over hva som er endret ved å fylle inn skjema under."
            />
        }

        <FormSection tabIndex={-1}>
            <div className="version-details">
                <label><span>Produkt:</span>
                    <FormInput
                        type={InputType.SELECT}
                        valueType="number"
                        tabIndex={1}
                        name="productId"
                        options={products.reduce((map: KeyValue<string>, product) => {
                            map[product.id] = product.name

                            return map
                        }, {})}
                    />
                </label>

                <label><span>Branch:</span>
                    <FormInput
                        type={InputType.SELECT}
                        tabIndex={2}
                        name="branchId"
                        options={product.branches.reduce((map: KeyValue<string>, branch) => {
                            map[branch.id] = branch.name

                            return map
                        }, {})}
                    />
                </label>

                <label>
                    <span>Versjon:</span>
                    <FormInput
                        tabIndex={3}
                        name="number"
                        placeholder="Versjon... (e.g. 1.0.0)"
                        pattern="^((?:[\d]+\.){2,3}[\d]+)$"
                    />
                </label>

                <label>
                    <span>Utgivelsesdato:</span>
                    <FormInput
                        type={InputType.DATE}
                        tabIndex={4}
                        optional={true}
                        name="released"
                    />
                </label>

                <label>
                    <span>Beskrivelse:</span>
                    <FormInput
                        type={InputType.TEXT_MULTI_LINE}
                        tabIndex={5}
                        optional={true}
                        name="description"
                        placeholder="Beskrivelse..."
                    />
                </label>
            </div>
        </FormSection>

        <FormSection>
            <FormSectionHeading>
                <h3>Utførte endringer</h3>
                <p>Under er en liste over endringer som er gjort i denne versjonen.</p>
            </FormSectionHeading>

            <div className="changes-list">
                {formData.changes.map((change: any | undefined, index: number) => {
                    if (!change)
                        return

                    return <div key={`change-index-${index}`} className="change-list-item">
                        <Button color="danger" className="remove-button" onClick={() => {
                            setFormData(current => {
                                const newValue = {...current}

                                delete newValue.changes[index]

                                return newValue
                            })
                        }}><i className="far fa-trash-can"/></Button>

                        <label><span>Tittel:</span>
                            <FormInput
                                name={`changes[${index}].title`}
                                optional={true}
                                placeholder="En tittel..."
                            />
                        </label>

                        <label><span>Beskrivelse:</span>
                            <FormInput
                                type={InputType.TEXT_MULTI_LINE}
                                name={`changes[${index}].content`}
                                placeholder="En liten beskrivelse av endringe..."
                            ></FormInput>
                        </label>
                    </div>
                })}
            </div>
        </FormSection>

        <FormSection className="flex row nowrap space-between">
            <div className="buttons-container left">
                <Button color="primary" onClick={() => {
                    setFormData(current => {

                        return {
                            ...current,
                            changes: [
                                ...current.changes,
                                {
                                    title: "",
                                    content: ""
                                }
                            ]
                        }
                    })
                }}><i className="far fa-plus"/> Ny endring</Button>
            </div>
            <div className="buttons-container right">
                <Button onClick={closeForm} color="default">Avbryt</Button>
                <Button type="submit" color="success">Lagre</Button>
            </div>
        </FormSection>
    </Form>
}
